import React, { useContext, useState } from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Textarea,
} from "@pushpress/pushpress-ui";
import { usePostOutreachSms } from "../../../apis/aiApi.hooks";
import { AppContext } from "../../../context/app";
import { CelebrationType } from "../../../apis/metricsApi";
import { useSendSMS } from "../../../apis/messagesApi.hooks";
import { BadgeColor, StatusChip } from "@pushpress/shared-components";
import { useSendOutreachSmsTask } from "../../../apis/taskApi.hooks";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { XIcon } from "lucide-react";
import { cn } from "../../../utils/css";
import { useDashboard } from "../DashboardProvider/DashboardProvider";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../utils/misc";
import { Icons } from "../../../icons";
import { getFirstAndLastInitials } from "../../../utils/names";

interface CommunicationMemberCardProps {
  recipientUuid: string;
  profilePic?: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  type: CelebrationType;
  celebrationSubheading: string;
  hasReceivedSms: boolean;
  numberOfYears?: number;
  onViewProfile: () => void;
}

export const CommunicationMemberCard: React.FC<
  CommunicationMemberCardProps
> = ({
  recipientUuid,
  profilePic,
  firstName,
  lastName,
  phoneNumber,
  type,
  celebrationSubheading,
  hasReceivedSms,
  numberOfYears,
  onViewProfile,
}) => {
  const app = useContext(AppContext);
  const { client } = app;
  const [message, setMessage] = useState("");
  const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(false);
  const celebrationCompletion = usePostOutreachSms();
  const sendOutreachTask = useSendOutreachSmsTask(recipientUuid, type);
  const sendSMS = useSendSMS();
  const { setLocalCelebrationsMetadataCache } = useDashboard();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCompletionLoading, setIsCompletionLoading] = useState(false);
  const [smsStatus, setSmsStatus] = useState({
    isSending: false,
    messageSent: false,
    smsAttempted: false,
  });
  const MAX_SMS_LENGTH = 160;

  const { trackEvent } = useAnalytics();

  const initials = getFirstAndLastInitials([firstName, lastName]);

  const handleGenerate = async (type: CelebrationType) => {
    const MAX_RETRIES = 2;
    let retryCount = 0;

    const attemptGeneration = async () => {
      const { completion: celebrationMessage } =
        await celebrationCompletion.mutateAsync({
          memberName: firstName,
          gymName: client?.company || "",
          numberOfYears,
          celebrationType: type,
        });

      return celebrationMessage;
    };

    try {
      setIsCompletionLoading(true);
      let celebrationMessage = "";

      while (retryCount < MAX_RETRIES) {
        celebrationMessage = await attemptGeneration();
        if (celebrationMessage.length <= MAX_SMS_LENGTH) {
          break;
        }
        retryCount++;
      }

      if (
        celebrationMessage === "" ||
        celebrationMessage.length > MAX_SMS_LENGTH
      ) {
        setIsCompletionLoading(false);
        toast.error(
          "Failed to generate message. Please try again.",
          defaultToastOptions,
        );
        return;
      }

      setMessage(celebrationMessage);
      setIsCompletionLoading(false);
      setHasAttemptedGeneration(true);
      trackEvent("AI Completion Succeeded", {
        type: type,
      });
    } catch (error) {
      trackEvent("AI Completion Failed", {
        type: type,
      });
    }
  };

  const showShowSentStatus = hasReceivedSms || smsStatus.messageSent;

  const handleSend = (phoneNumber: string) => {
    // Optimistically update the status
    setSmsStatus({
      isSending: false,
      messageSent: true,
      smsAttempted: true,
    });

    const concatenatedMessage = message.slice(0, MAX_SMS_LENGTH);
    setMessage("");
    setHasAttemptedGeneration(false);
    sendSMS.mutate(
      {
        message: concatenatedMessage,
        to: phoneNumber,
      },
      {
        onSuccess: () => {
          setLocalCelebrationsMetadataCache((prev) => [
            ...prev,
            {
              userUuid: recipientUuid,
              eventType: type,
            },
          ]);
          sendOutreachTask.mutate({
            message: {
              content: concatenatedMessage,
            },
            recipient: {
              uuid: recipientUuid,
              name: `${firstName} ${lastName}`,
              phone: phoneNumber,
            },
            event: {
              type,
              date: new Date().toISOString(),
            },
          });
        },
        onError: () => {
          toast.error("Failed to send message", defaultToastOptions);
          setSmsStatus({
            isSending: false,
            messageSent: false,
            smsAttempted: true,
          });
        },
      },
    );
    trackEvent("AI Generated Outreach Sent", {
      category: type,
      type: "sms",
    });
    setIsExpanded(false);
  };

  return (
    <div
      className={cn(
        "hover:tw-bg-gray-50 hover:tw-border-gray-200 tw-border tw-border-transparent tw-rounded-lg tw-p-3 tw-cursor-pointer",
      )}
    >
      <div
        onClick={onViewProfile}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onViewProfile();
          }
        }}
        role="button"
        tabIndex={0}
        className="tw-flex tw-flex-col tw-gap-4"
      >
        <div className="tw-flex tw-items-center">
          <Avatar className="tw-border">
            <AvatarImage src={profilePic} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="tw-flex-grow tw-ml-2">
            <p className="tw-text-sm tw-font-medium">{`${firstName} ${lastName}`}</p>
            <p className="tw-text-xs tw-text-gray-500">
              {celebrationSubheading}
            </p>
          </div>
          <div>
            {showShowSentStatus && (
              <StatusChip
                label="sent"
                color={BadgeColor.Success}
                size="small"
              />
            )}
          </div>
          {!isExpanded && !showShowSentStatus && (
            <div
              className={cn(
                "tw-flex tw-items-center tw-justify-center tw-gap-2",
              )}
            >
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    setIsExpanded(true);
                  }
                }}
                role="button"
                tabIndex={0}
                className="tw-flex tw-flex-col"
              >
                <Button
                  size="sm"
                  variant="outline"
                  className={"tw-flex tw-items-center tw-justify-center"}
                >
                  <p className="tw-text-gray-600">Send SMS</p>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="tw-mt-1 tw-relative">
          <div className="tw-flex tw-justify-end tw-mb-1">
            <Button variant="ghost" onClick={() => setIsExpanded(false)}>
              <XIcon className="tw-h-4 tw-w-4 text-gray-500" />
            </Button>
          </div>
          <div className="tw-relative">
            <Textarea
              rows={5}
              maxLength={MAX_SMS_LENGTH}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                isCompletionLoading
                  ? "Generating message..."
                  : "Enter your message..."
              }
              disabled={isCompletionLoading}
              className={cn(
                "tw-bg-white",
                isCompletionLoading && "tw-opacity-10",
              )}
            />
            {isCompletionLoading && (
              <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/50">
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                  <div className="tw-relative tw-w-8 tw-h-8">
                    <div className="tw-absolute tw-top-0 tw-left-0 tw-w-8 tw-h-8 tw-border-2 tw-border-gray-200 tw-rounded-full"></div>
                    <div
                      className="tw-absolute tw-top-0 tw-left-0 tw-w-8 tw-h-8 tw-border-2 tw-border-blue-500 tw-rounded-full tw-animate-spin"
                      style={{
                        borderTopColor: "transparent",
                        animationDuration: "1s",
                        animationTimingFunction: "linear",
                        animationIterationCount: "infinite",
                      }}
                    />
                  </div>

                  <span className="tw-text-sm tw-text-black">
                    Generating message...
                  </span>
                </div>
              </div>
            )}
            <p className="tw-text-xs tw-text-gray-400 tw-mt-2 tw-text-right">
              {message.length} / {MAX_SMS_LENGTH}
            </p>
          </div>
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleGenerate(type)}
              disabled={isCompletionLoading}
            >
              {isCompletionLoading ? (
                <span>Generating...</span>
              ) : (
                <div className="tw-flex tw-items-center tw-gap-1">
                  <Icons.Sparkles className="tw-w-3 tw-h-3 tw-text-yellow-400" />{" "}
                  {hasAttemptedGeneration ? "Regenerate" : "Generate"}
                </div>
              )}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => handleSend(phoneNumber)}
              disabled={isCompletionLoading || !message}
            >
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
